/**
 * Legacy TipNet Query Parser
 *
 * Converts a flat TipNet detail response array into a native { QueryInfo, Query }
 * shape identical to what /rest/savedquery/chart/query/copy returns, so that
 * processLegacyQuery in QueryResult can reuse the same downstream setup/run path.
 *
 * See: src/utils/legacyTipNetParser.md for full analysis and unknowns.
 */

// ---------------------------------------------------------------------------
// Lookup tables — extend as more source_types / dimensions / measures are seen
// ---------------------------------------------------------------------------

interface CubeInfo {
  cubeName: string;
  schemaName: string;
}

/**
 * source_type (from list row) → native cube identifiers
 * UNKNOWN entries: add when new source types are encountered in the field.
 */
const SOURCE_TYPE_TO_CUBE: Record<string, CubeInfo> = {
  "New Registration": { cubeName: "NVR_PLK", schemaName: "New Registration PLK" },
  "Vehicle In Operation": { cubeName: "VIO_PLK", schemaName: "VIO PLK" },
  "New Trailer": { cubeName: "TRAILER_PLK", schemaName: "New Trailer PLK" },
};

/**
 * (cat_desc, parm_desc) → MDX unique name for enhancedFilterCriteria keys.
 *
 * Key format: `${cat_desc}:${parm_desc}` — both are human-readable strings
 * taken directly from the TipNet detail API row. Using descriptive names makes
 * the mapping stable against TipNet renumbering cat_id / parm_id.
 *
 * cat_desc    parm_desc  → MDX dimension
 * ----------  ---------  ----------------
 * Std Geo.    State      [Standard Geo].[Standard Geo].[State]
 * MSM         Make       [VehAttr].[VehAttr].[Make]
 *
 * UNKNOWN entries: add as more dimension/parameter combinations are seen.
 */
const DIM_FILTER_MAP: Record<string, string> = {
    "Std Geo.:Entire U.S."                  : "[Standard Geo].[Standard Geo].[Country]",   //pass
    "Std Geo.:Entire Puerto Rico"           : "[Standard Geo].[Standard Geo].[Country]",   //pass
    "Std Geo.:State"                        : "[Standard Geo].[Standard Geo].[State]",     //pass
    "Std Geo.:County"                       : "[Standard Geo].[Standard Geo].[County]",    //pass not multi select.
    "Std Geo.:ZIP Base"                     : "[Standard Geo].[Standard Geo].[ZIP]",       //pass - with "StartsWith" filter shape to capture all ZIPs in the base
    "Std Geo.:ZIP Code"                     : "[Standard Geo].[Standard Geo].[ZIP]",       //pass      
    "Std Geo.:ZIP/FSA (TYPING)"             : "[Standard Geo].[Standard Geo].[ZIP]",        
    "Std Geo.:DMA"                          : "[Standard Geo].[Standard Geo].[DMA]",       //pass DMA code is missing alot from CVI so use DMA name
    "Std Geo.:CBSA"                         : "[Standard Geo].[Standard Geo].[CBSA Code]", //pass tipnet are combine, so only CBSA code is used for mapping to CVI
    "Std Geo.:Canada"                       : "[Standard Geo].[Standard Geo].[Country]",
    
    // ── MSM (Make / Series / Model) ───────────────────────────────────────────────
    "MSM:Make"                              : "[VehAttr].[VehAttr].[Make]",                 //pass
    //there is legacy bug from tipnet UI, where modle and series are swapped in the display name vs. the actual value, but json file has the correct mapping, so we will follow the json value for mapping to CVI and ignore the display name swap bug.
    "MSM:Series"                           : "[VehAttr].[VehAttr].[Series]",
    "MSM:Model"                           : "[VehAttr].[VehAttr].[Model]",
    "MSM:Veh. Co. Name"                     : "[VehAttr].[VehAttr].[Corporate Mfgr Name]",
    
    // ── Detail (Vehicle Attributes) ───────────────────────────────────────────────
    "Detail:Bed Length"                     : "[VehAttr].[VehAttr].[Bed Length]",           //pass
    "Detail:Brakes"                         : "[VehAttr].[VehAttr].[Brake Type]",           //pass
    "Detail:Cab Config."                    : "[VehAttr].[VehAttr].[Cab Type]",           //pass
    "Detail:CID"                            : "[VehAttr].[VehAttr].[Engine Displacement (CI)]", //pass
    "Detail:Cylinders"                      : "[VehAttr].[VehAttr].[Cylinders]",                //pass
    "Detail:Engine Mfr."                    : "[VehAttr].[VehAttr].[Engine Manufacturer]",
    "Detail:Engine Model"                   : "[VehAttr].[VehAttr].[Engine Model]",
    "Detail:Engine Type"                    : "[VehAttr].[VehAttr].[Engine Type]",
    "Detail:Front Axle"                     : "[VehAttr].[VehAttr].[Front Axles]",
    "Detail:Fuel"                           : "[VehAttr].[VehAttr].[Fuel]",
    "Detail:Lite Indicator"                 : "PLACEHOLDER",                                       // Lite truck indicator – no matching level in VehAttr
    "Detail:Liters"                         : "[VehAttr].[VehAttr].[Engine Displacement (Liters)]",
    "Detail:MFR GVW"                        : "[VehAttr].[VehAttr].[MFR GVW]",                    // label: "MFG GVW Class"
    "Detail:Rear Axle"                      : "[VehAttr].[VehAttr].[Rear Axles]",
    "Detail:Vehicle Type"                   : "[VehAttr].[VehAttr].[Vehicle Type]",
    "Detail:VIN"                            : "[FactAttr].[FactAttr].[VIN]",
    "Detail:VIN GVW"                        : "[VehAttr].[VehAttr].[GVW]",                        // label: "VIN GVW Class"
    "Detail:Wheels"                         : "[VehAttr].[VehAttr].[Wheels]",
    "Detail:Year Model"                     : "[VehAttr].[VehAttr].[Model Year]",
    "Detail:Sleeper Units"                  : "[VehAttr].[VehAttr].[Sleeper Indicator]",
    "Detail:Upfit"                          : "[FactAttr].[FactAttr].[Upfit]",
};

/**
 * Measure name (value of sub_parm="020" row with "Field: " prefix stripped)
 * within cat_desc="Display" / type="Count Field" rows → MDX measure (COLUMNS)
 *
 * stripped key       → MDX measure
 * ---------------    ----------------
 * Standard Count     [Measures].[Vehicle Count]
 *
 * UNKNOWN entries: add as more measure names are seen.
 */
const MEASURE_MAP: Record<string, string> = {
  "Standard Count": "[Measures].[Vehicle Count]",
  // TODO: other measures
};

/**
 * Display field name (value of sub_parm="020" row with "Field: " prefix stripped)
 * within cat_desc="Display" / type="Data Field" rows → MDX dimension (ROWS)
 *
 * The short name comes directly from the TipNet value string, e.g.:
 *   "Field: Make"       → "Make"       → [VehAttr].[VehAttr].[Make]
 *   "Field: Bed Length" → "Bed Length" → [VehAttr].[VehAttr].[Bed Length]
 *
 * UNKNOWN entries: add as more display field names are seen.
 */
const DISPLAY_FIELD_MAP: Record<string, string> = {
  // ── Standard Geography (category 5) ───────────────────────────────────────
  "Registration City"                        : "[Standard Geo].[Standard Geo].[City]",
  "Registration County"                      : "[Standard Geo].[Standard Geo].[County]",
  "Registration State/Province"              : "[Standard Geo].[Standard Geo].[State]",
  "Registration State/Province Abbreviation" : "[Standard Geo].[Standard Geo].[State Abbrv]",
  "Registration ZIP/FSA Code"                : "[Standard Geo].[Standard Geo].[ZIP]",
  "Registration ZIP Base/Postal Code Base"   : "[Standard Geo].[Standard Geo].[ZIP]",
  "DMA®"                                     : "[Standard Geo].[Standard Geo].[DMA]",
  "DMA® Code"                                : "[Standard Geo].[Standard Geo].[DMA Code]",
  "CBSA"                                     : "[Standard Geo].[Standard Geo].[CBSA Name]",
  "CBSA Code"                                : "[Standard Geo].[Standard Geo].[CBSA Code]",
  "Country Name"                             : "[Standard Geo].[Standard Geo].[Country]",
  "Plant Location"                           : "[VehAttr].[VehAttr].[Plant Location]",
  // ── Make / Series / Model (category 4 type 2) ─────────────────────────────
  "Make"                                     : "[VehAttr].[VehAttr].[Make]",
  "Series"                                   : "[VehAttr].[VehAttr].[Series]",
  "Model"                                    : "[VehAttr].[VehAttr].[Model]",
  "Vehicle Company Name"                     : "[VehAttr].[VehAttr].[Corporate Mfgr Name]",
  // ── Vehicle Details (category 4) ──────────────────────────────────────────
  "Bed Length"                               : "[VehAttr].[VehAttr].[Bed Length]",
  "Brakes"                                   : "[VehAttr].[VehAttr].[Brake Type]",
  "Brake Alpha Code"                         : "[VehAttr].[VehAttr].[Brake Type]",
  "Cab Configuration"                        : "[VehAttr].[VehAttr].[Cab Type]",
  "Cab Configuration Code"                   : "[VehAttr].[VehAttr].[Cab Type]",
  "CID"                                      : "[VehAttr].[VehAttr].[Engine Displacement (CI)]",
  "Cylinders"                                : "[VehAttr].[VehAttr].[Cylinders]",
  "Engine Manufacturer"                      : "[VehAttr].[VehAttr].[Engine Manufacturer]",
  "Engine Mfg Alpha Code"                    : "[VehAttr].[VehAttr].[Engine Manufacturer]",
  "Engine Model"                             : "[VehAttr].[VehAttr].[Engine Model]",
  "Engine Type"                              : "[VehAttr].[VehAttr].[Engine Type]",
  "Engine Type Alpha Code"                   : "[VehAttr].[VehAttr].[Engine Type]",
  "Front Axle"                               : "[VehAttr].[VehAttr].[Front Axles]",
  "Fuel"                                     : "[VehAttr].[VehAttr].[Fuel]",
  "Fuel (Gas/Flex)"                          : "[VehAttr].[VehAttr].[Fuel]",
  "Fuel Alpha Code"                          : "[VehAttr].[VehAttr].[Fuel]",
  "Liters"                                   : "[VehAttr].[VehAttr].[Engine Displacement (Liters)]",
  "MFR GVW"                                  : "[VehAttr].[VehAttr].[MFR GVW]",
  "Rear Axle"                                : "[VehAttr].[VehAttr].[Rear Axles]",
  "Sleeper"                                  : "[VehAttr].[VehAttr].[Sleeper Indicator]",
  "Vehicle Type"                             : "[VehAttr].[VehAttr].[Vehicle Type]",
  "Vehicle Type Alpha Code"                  : "[VehAttr].[VehAttr].[Vehicle Type]",
  "VIN"                                      : "[FactAttr].[FactAttr].[VIN]",
  "VIN GVW"                                  : "[VehAttr].[VehAttr].[GVW]",
  "Upfit"                                    : "[FactAttr].[FactAttr].[Upfit]",
  "Wheels"                                   : "[VehAttr].[VehAttr].[Wheels]",
  "Year Model"                               : "[VehAttr].[VehAttr].[Model Year]",
  // ── Owner / Fleet (category 2, 3, 8) ──────────────────────────────────────
  "Registration Name"                        : "[FleetAttr].[FleetAttr].[Name]",
  "Registration Address"                     : "[FleetAttr].[FleetAttr].[Fleet Address]",
  "Registration Local Fleet Size"            : "[FleetAttr].[FleetAttr].[Local Fleet Size]",
  "Registration National Fleet Size"         : "[FleetAttr].[FleetAttr].[National Fleet Size]",
  "Registration Carrier Type"               : "[FleetVocationAttr].[FleetVocationAttr].[Carrier Type]",
  "Registration Vocation"                    : "[FleetVocationAttr].[FleetVocationAttr].[Vocation]",
  "Secondary Name"                           : "[FleetAttr].[FleetAttr].[Secondary Name]",
  "Business Secondary Name"                  : "[FleetAttr].[FleetAttr].[Secondary Name]",
  // ── Business / D&B (category 10) ──────────────────────────────────────────
  "Business Name"                            : "[FleetDnbAttr].[FleetDnbAttr].[Company Name]",
  "Business Employee Size"                   : "[FleetDnbAttr].[FleetDnbAttr].[Employee Size]",
  "Business NAICS Code"                      : "[FleetDnbAttr].[FleetDnbAttr].[NAICS]",
  "Business NAICS Descr"                     : "[FleetDnbAttr].[FleetDnbAttr].[NAICS]",
  "Business SIC2 Code"                       : "[FleetDnbAttr].[FleetDnbAttr].[SIC2 Code]",
  "Business SIC2 Desc"                       : "[FleetDnbAttr].[FleetDnbAttr].[SIC2 Desc]",
  "Business SIC4 Code"                       : "[FleetDnbAttr].[FleetDnbAttr].[SIC4 Code]",
  "Business SIC4 Desc"                       : "[FleetDnbAttr].[FleetDnbAttr].[SIC4 Desc]",
  "Business Sales Volume"                    : "[FleetDnbAttr].[FleetDnbAttr].[Sales Volume]",
  // ── Selling Dealer (category 11) ──────────────────────────────────────────
  "Selling Dlr Name"                         : "[DealerAttr].[DealerAttr].[Selling Dlr Name]",
  "Selling Dlr State"                        : "[DealerAttr].[DealerAttr].[Selling Dlr State]",
  "Selling Dlr ZIP"                          : "[DealerAttr].[DealerAttr].[Selling Dlr Zip]",
  // ── Self Reported / DOT (category 12, 13) ─────────────────────────────────
  "DOT Number"                               : "[FactAttr].[FactAttr].[DOT Number]",
};

/**
 * State abbreviation → full uppercase name for County value transformation.
 * TipNet sends "MI, OAKLAND"; CVI expects "MICHIGAN MI:OAKLAND".
 */
const STATE_ABBREV_TO_NAME: Record<string, string> = {
  AL: "ALABAMA",
  AK: "ALASKA",
  AZ: "ARIZONA",
  AR: "ARKANSAS",
  CA: "CALIFORNIA",
  CO: "COLORADO",
  CT: "CONNECTICUT",
  DE: "DELAWARE",
  DC: "DISTRICT OF COLUMBIA",
  FL: "FLORIDA",
  GA: "GEORGIA",
  HI: "HAWAII",
  ID: "IDAHO",
  IL: "ILLINOIS",
  IN: "INDIANA",
  IA: "IOWA",
  KS: "KANSAS",
  KY: "KENTUCKY",
  LA: "LOUISIANA",
  ME: "MAINE",
  MD: "MARYLAND",
  MA: "MASSACHUSETTS",
  MI: "MICHIGAN",
  MN: "MINNESOTA",
  MS: "MISSISSIPPI",
  MO: "MISSOURI",
  MT: "MONTANA",
  NE: "NEBRASKA",
  NV: "NEVADA",
  NH: "NEW HAMPSHIRE",
  NJ: "NEW JERSEY",
  NM: "NEW MEXICO",
  NY: "NEW YORK",
  NC: "NORTH CAROLINA",
  ND: "NORTH DAKOTA",
  OH: "OHIO",
  OK: "OKLAHOMA",
  OR: "OREGON",
  PA: "PENNSYLVANIA",
  PR: "PUERTO RICO",
  RI: "RHODE ISLAND",
  SC: "SOUTH CAROLINA",
  SD: "SOUTH DAKOTA",
  TN: "TENNESSEE",
  TX: "TEXAS",
  UT: "UTAH",
  VT: "VERMONT",
  VA: "VIRGINIA",
  WA: "WASHINGTON",
  WV: "WEST VIRGINIA",
  WI: "WISCONSIN",
  WY: "WYOMING",
};

/**
 * Transform a TipNet county value into CVI format.
 * "MI, OAKLAND" → "MICHIGAN MI:OAKLAND"
 */
function transformCountyValue(raw: string): string {
  const commaIdx = raw.indexOf(", ");
  if (commaIdx === -1) return raw; // unexpected format — pass through unchanged
  const abbrev = raw.slice(0, commaIdx);
  const county = raw.slice(commaIdx + 2);
  const fullName = STATE_ABBREV_TO_NAME[abbrev] ?? abbrev;
  return `${fullName} ${abbrev}:${county}`;
}

/**
 * Transform a TipNet country value into CVI format.
 * "U.S." → "USA"
 */
const COUNTRY_VALUE_MAP: Record<string, string> = {
  "U.S.": "USA",
  "Canada": "CANADA",
  "Puerto Rico": "PUERTO RICO",
};

function transformCountryValue(raw: string): string {
  return COUNTRY_VALUE_MAP[raw] ?? raw;
}

/**
 * Per-MDX-dimension value transformer.
 * Applied to each raw TipNet value before storing in enhancedFilterCriteria.
 */
const VALUE_TRANSFORMER_MAP: Record<string, (v: string) => string> = {
  "[Standard Geo].[Standard Geo].[County]": transformCountyValue,
  "[Standard Geo].[Standard Geo].[Country]": transformCountryValue,
};

/**
 * Per-input-key filter shape overrides.
 * Keys are the same "cat_desc:parm_desc" compound keys used in DIM_FILTER_MAP.
 * When absent, the default shape (Selection / in) is used.
 */
const FILTER_SHAPE_MAP: Record<string, { filterCategory: "Selection" | "WildCard"; condition: "in" | "StartsWith" }> = {
  "Std Geo.:ZIP Base"        : { filterCategory: "WildCard", condition: "StartsWith" },
};

const DEFAULT_FILTER_SHAPE = { filterCategory: "Selection" as const, condition: "in" as const };

/**
 * Per-input-key value transformer (compound key overrides).
 * Takes precedence over VALUE_TRANSFORMER_MAP when both would apply.
 * Use this when two keys share the same MDX dim but need different value transforms.
 */
const KEY_VALUE_TRANSFORMER_MAP: Record<string, (v: string) => string> = {
  // ZIP Code values come as "48302, BLOOMFLD HLS" — extract only the ZIP prefix before the comma
  "Std Geo.:ZIP Code": (raw: string) => raw.split(",")[0].trim(),
  // DMA values come as "144, COLUMBUS OH" — extract only the name after the comma
  "Std Geo.:DMA"     : (raw: string) => raw.split(", ").slice(1).join(", ").trim(),
  // CBSA values come as "10940, ALMA, MI" — extract only the numeric code before the first comma
  "Std Geo.:CBSA"    : (raw: string) => raw.split(",")[0].trim(),
  // CID values are space-padded (" 110"); trim to get the plain number. "UNK" → "UNKNOWN"
  "Detail:CID"       : (raw: string) => { const t = raw.trim(); return t === "UNK" ? "#null" : t; },
  // Cylinders "U" or "UN" → "UNKNOWN"
  "Detail:Cylinders" : (raw: string) => { const t = raw.trim(); return (t === "U" || t === "UN") ? "#null" : t; },
};

/**
 * Default time period MDX unique name. Always injected into COLUMNS[0] and
 * enhancedFilterCriteria when no explicit time period row is present in the detail.
 */
const TIME_PERIOD_MDX = "[New Time Period].[New Time Period].[Time Periods]";
const DEFAULT_TIME_PERIOD_VALUE = "CYTD";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TipNetDetailRow {
  cat_id: string;
  cat_desc: string;
  parm_id: string;
  parm_desc: string;
  value: string;
  value_code: string;
  sub_parm: string;
  cat_sort: string;
  parm_sort: string;
  value_sort: string;
}

interface FilterValue {
  selType: "None";
  value: string;
  namedSet: false;
}

interface FilterCriteria {
  condition: "in" | "StartsWith";
  values: FilterValue[];
  incExc: boolean;
  joiner: "NOOP";
}

interface FilterCriteriaEntry {
  filterCategory: "Selection" | "WildCard";
  criteria: FilterCriteria[];
}

export interface ParsedLegacyQuery {
  QueryInfo: {
    type: "Grid";
    subType: null;
    gridSorts: null;
    mapOptions: null;
    name: string;
  };
  Query: {
    name: string;
    cubeName: string;
    schemaName: string;
    axes: { ROWS: string[]; COLUMNS: string[] };
    enhancedFilterCriteria: Record<string, FilterCriteriaEntry[]>;
    properties: Record<string, never>;
    gridSorts: never[];
    owner: string;
    groupFilters: null;
  };
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

/**
 * Parse a flat TipNet detail array into a native { QueryInfo, Query } object.
 *
 * @param detail      - Raw array from GET /tipnet-api/rest/api/query/{corp}/{email}/{saved_id}
 * @param sourceType  - `source_type` from the list row that was clicked (e.g. "N")
 * @param savedDesc   - `saved_desc` from the list row (becomes query name / tab title)
 */
export function parseLegacyTipNetQuery(
  detail: TipNetDetailRow[],
  sourceType: string,
  savedDesc: string,
): ParsedLegacyQuery {
  // --- Resolve cube ---------------------------------------------------------
  const cubeInfo = SOURCE_TYPE_TO_CUBE[sourceType];
  if (!cubeInfo) {
    console.warn(
      `[legacyTipNetParser] Unknown source_type "${sourceType}". Cube mapping missing.`,
    );
  }
  const cubeName = cubeInfo?.cubeName ?? "";
  const schemaName = cubeInfo?.schemaName ?? "";

  // --- Group rows by (cat_desc, parm_desc) -----------------------------------
  const groups = new Map<string, TipNetDetailRow[]>();
  for (const row of detail) {
    const key = `${row.cat_desc}:${row.parm_desc}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(row);
  }

  const enhancedFilterCriteria: Record<string, FilterCriteriaEntry[]> = {};
  const measureColumns: string[] = [];
  // rowEntries accumulates { parm_sort, mdxPath } so we can sort by field order
  const rowEntries: { parmSort: string; mdxPath: string }[] = [];
  let hasTimePeriod = false;

  for (const [key, rows] of groups.entries()) {
    // --- cat_desc="Display" → Data Field (ROWS) or Count Field (COLUMNS) ---
    if (rows[0].cat_desc === "Display") {
      const fieldRow = rows.find((r) => r.sub_parm === "020");
      const fieldName = fieldRow?.value?.replace(/^Field:\s*/, "").trim();
      if (!fieldName) continue;

      const isDataField = rows.some((r) => r.value === "Type: Data Field");
      const isCountField = rows.some((r) => r.value === "Type: Count Field");

      if (isDataField) {
        const mdxPath = DISPLAY_FIELD_MAP[fieldName];
        if (mdxPath) {
          rowEntries.push({ parmSort: rows[0].parm_sort, mdxPath });
        } else {
          console.warn(
            `[legacyTipNetParser] Unknown display field "${fieldName}" — skipped from ROWS.`,
          );
        }
      } else if (isCountField) {
        const mdxMeasure = MEASURE_MAP[fieldName];
        if (mdxMeasure) {
          measureColumns.push(mdxMeasure);
        } else {
          console.warn(
            `[legacyTipNetParser] Unknown measure "${fieldName}" — skipped from COLUMNS.`,
          );
        }
      }
      continue;
    }

    // --- Filter dimension rows ---------------------------------------------
    const mdxDim = DIM_FILTER_MAP[key];  // key is "cat_desc:parm_desc"
    if (!mdxDim) {
      console.warn(
        `[legacyTipNetParser] Unknown dimension "${key}" — skipped.`,
      );
      continue;
    }

    // Split into value rows vs. the include/exclude flag row
    // The flag row has value === "Include" or value === "Exclude"
    const flagRow = rows.find(
      (r) => r.value === "Include" || r.value === "Exclude",
    );
    const valueRows = rows.filter(
      (r) => r.value !== "Include" && r.value !== "Exclude",
    );
    const incExc = flagRow?.value !== "Exclude"; // default include if flag absent

    // TODO: detect time period cat_id when a real example is available
    // For now, check if the MDX dim is TIME_PERIOD_MDX
    if (mdxDim === TIME_PERIOD_MDX) {
      hasTimePeriod = true;
    }

    const transformer = KEY_VALUE_TRANSFORMER_MAP[key] ?? VALUE_TRANSFORMER_MAP[mdxDim];
    const filterValues: FilterValue[] = valueRows.map((r) => ({
      selType: "None",
      value: transformer ? transformer(r.value) : r.value,
      namedSet: false,
    }));

    if (filterValues.length === 0) continue;

    const shape = FILTER_SHAPE_MAP[key] ?? DEFAULT_FILTER_SHAPE;
    const newEntry: FilterCriteriaEntry = {
      filterCategory: shape.filterCategory,
      criteria: [{ condition: shape.condition, values: filterValues, incExc, joiner: "NOOP" }],
    };

    if (enhancedFilterCriteria[mdxDim]) {
      // Find an existing entry with the same filterCategory + condition to merge values into.
      // If none matches (e.g. ZIP Base WildCard vs ZIP Code Selection), push a new entry.
      const existing = enhancedFilterCriteria[mdxDim].find(
        (e) => e.filterCategory === shape.filterCategory && e.criteria[0]?.condition === shape.condition,
      );
      if (existing) {
        existing.criteria[0].values.push(...filterValues);
      } else {
        enhancedFilterCriteria[mdxDim].push(newEntry);
      }
    } else {
      enhancedFilterCriteria[mdxDim] = [newEntry];
    }
  }

  // --- Inject default time period if not present ----------------------------
  if (!hasTimePeriod) {
    enhancedFilterCriteria[TIME_PERIOD_MDX] = [
      {
        filterCategory: "Selection",
        criteria: [
          {
            condition: "in",
            values: [{ selType: "None", value: DEFAULT_TIME_PERIOD_VALUE, namedSet: false }],
            incExc: true,
            joiner: "NOOP",
          },
        ],
      },
    ];
  }

  // --- Build ROWS: sort by parm_sort (field order from TipNet) --------------
  rowEntries.sort((a, b) => a.parmSort.localeCompare(b.parmSort));
  const rows_axes = rowEntries.map((e) => e.mdxPath);

  // --- Build COLUMNS: time period first, then measures ---------------------
  const columns: string[] = [TIME_PERIOD_MDX, ...measureColumns];

  return {
    QueryInfo: {
      type: "Grid",
      subType: null,
      gridSorts: null,
      mapOptions: null,
      name: savedDesc,
    },
    Query: {
      name: "", // replaced by newQuery() server response in processLegacyQuery
      cubeName,
      schemaName,
      axes: {
        ROWS: rows_axes,
        COLUMNS: columns,
      },
      enhancedFilterCriteria,
      properties: {},
      gridSorts: [],
      owner: "", // replaced by currentUser in processLegacyQuery
      groupFilters: null,
    },
  };
}
